import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * Patient Context für Chat-Links und Kontext-Sharing
 * Speichert aktuelle Patient/Exam-Informationen aus verschiedenen Komponenten
 */

const PatientContext = createContext();

export const usePatientContext = () => {
  const context = useContext(PatientContext);
  if (!context) {
    throw new Error('usePatientContext must be used within PatientContextProvider');
  }
  return context;
};

export const PatientContextProvider = ({ children }) => {
  const [currentContext, setCurrentContext] = useState(null);

  /**
   * Setzt den aktuellen Patientenkontext
   * @param {Object} context - Kontextinformationen
   * @param {string} context.type - 'approval' oder 'report'
   * @param {number} context.examId - Exam/Report ID
   * @param {string} context.patientName - Patientenname
   * @param {string} context.patientId - Patienten ID (optional)
   * @param {string} context.studyDate - Untersuchungsdatum (optional)
   * @param {string} context.modality - Modalität (optional)
   */
  const setContext = useCallback((context) => {
    setCurrentContext(context);
  }, []);

  /**
   * Löscht den aktuellen Kontext
   */
  const clearContext = useCallback(() => {
    setCurrentContext(null);
  }, []);

  /**
   * Erstellt einen Chat-Link basierend auf aktuellem Kontext
   * @returns {Object|null} Link-Objekt mit { text, url, type, examId, reportId, patientName }
   */
  const createContextLink = useCallback(() => {
    if (!currentContext) return null;

    const { type, examId, reportId, patientName } = currentContext;
    
    let url = '';
    let contextType = '';

    if (type === 'approval') {
      url = `/approval?examId=${examId}`;
      contextType = 'approval';
    } else if (type === 'report') {
      url = `/report/${examId}`;
      contextType = 'report';
    }

    return {
      text: patientName,
      url,
      type: contextType,
      examId,
      reportId, // Füge reportId hinzu (falls vorhanden)
      patientName,
      ...currentContext // Include alle anderen Kontextinformationen
    };
  }, [currentContext]);

  const value = {
    currentContext,
    setContext,
    clearContext,
    createContextLink,
    hasContext: !!currentContext
  };

  return (
    <PatientContext.Provider value={value}>
      {children}
    </PatientContext.Provider>
  );
};

